import styled from '@emotion/styled'
import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, RefreshMetricsByTime } from '@pubstack/common/src/analytics/query'
import { useAtom } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { LazyVisible } from '~/components/LazyVisible'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useBreakdownTabs } from '~/hooks/useBreakdownTabs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { contextState } from '~/state'
import { onBreakdownRowClick } from '~/utils/analytics'
import { PureRefreshTimeline } from './PureRefreshTimeline'
import { RefreshBreakdown } from './RefreshBreakdown'
import { RefreshRank } from './RefreshRank'

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 16px;
  }
`

const DIMENSIONS: Dimension[] = ['tagId', 'bidder', 'adUnit', 'device', 'country', 'size', 'pubstackRefresh', 'abTestPopulation', 'stackIdVersion']
const DASHBOARD: AnalyticsQueryDashboard = { name: 'refresh', filterType: 'auctions' }

export const RefreshPage: FunctionComponent = () => {
  const currencySymbol = useScopeCurrency()
  const user = useAuthenticatedUser()
  const tabsConfig = useBreakdownTabs(user, ['tagId', 'bidder', 'adUnit', 'device', 'country', 'size'], { abTestPopulation: 'abtest', stackIdVersion: 'adstack' })
  const [context, setContext] = useAtom(contextState)
  const [breakdownDimension, setBreakdownDimension] = useState<Dimension>('tagId')

  const analyticsQueryByTime = useAnalyticsQueryTanstack<RefreshMetricsByTime>('refresh.metrics.by.time', DIMENSIONS, context, {})

  const onBreakdownTabChange = (dimension: Dimension) => {
    setBreakdownDimension(dimension)
  }

  const onMetricChange = (_metric: string) => {}

  const breadcrumbs = useBreadcrumbs()
  return (
    <AnalyticsPage
      onRefreshClick={() => {
        analyticsQueryByTime.refetch()
      }}
      filterDimensions={DIMENSIONS}
      dashboard={DASHBOARD}
      title={breadcrumbs}
    >
      <PageContent>
        <PureRefreshTimeline
          isLoading={analyticsQueryByTime.isFetching}
          error={!!analyticsQueryByTime.error}
          empty={!analyticsQueryByTime.isLoading && !analyticsQueryByTime?.data?.epoch.length}
          onRefreshClick={() => analyticsQueryByTime.refetch()}
          data={analyticsQueryByTime.data ?? { epoch: [] }}
          currencySymbol={currencySymbol}
          currentEpoch={DateTime.now()}
          timezone={context.timeRange.tz}
          onMetricChange={onMetricChange}
        />
        <LazyVisible minHeight={400}>
          <RefreshRank context={context} dimensions={DIMENSIONS} currencySymbol={currencySymbol} />
        </LazyVisible>
        <LazyVisible minHeight={400}>
          <RefreshBreakdown
            context={context}
            dimensions={DIMENSIONS}
            dimension={breakdownDimension}
            currencySymbol={currencySymbol}
            onTabChange={onBreakdownTabChange}
            onRowClick={onBreakdownRowClick(breakdownDimension, setContext)}
            tabsConfig={tabsConfig}
          />
        </LazyVisible>
      </PageContent>
    </AnalyticsPage>
  )
}
